import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAPIToken } from "@eaigle/ui";
import { apiClient } from "../services/api-client";
import {
  DeploymentAccessContext,
  type DeploymentAccessStatus,
} from "./deployment-access-context";

type DeploymentAccessProviderProps = {
  children: ReactNode;
};

export function DeploymentAccessProvider({ children }: DeploymentAccessProviderProps) {
  const { token } = useAPIToken();
  const [status, setStatus] = useState<DeploymentAccessStatus>("loading");

  const refreshAccess = useCallback(async () => {
    if (!token) {
      setStatus("loading");
      return;
    }
    try {
      await apiClient.getHistory(token);
      setStatus("granted");
    } catch (error) {
      const httpStatus = (error as { response?: { status?: number } }).response?.status;
      setStatus(httpStatus === 403 ? "forbidden" : "error");
    }
  }, [token]);

  useEffect(() => {
    void refreshAccess();
  }, [refreshAccess]);

  const value = useMemo(
    () => ({
      status,
      hasAdminAccess: status === "granted",
      refreshAccess,
    }),
    [status, refreshAccess],
  );

  return (
    <DeploymentAccessContext.Provider value={value}>
      {children}
    </DeploymentAccessContext.Provider>
  );
}
